<template>
  <div class="tender-communications-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="email" color="accent" class="q-mr-sm" />
            Communications
          </div>
          <div class="panel-subtitle">
            Manage emails, comments, and communication history for this tender
          </div>
        </div>
        <div class="col-auto">
          <q-btn-group>
            <q-btn
              color="accent"
              icon="email"
              label="Send Email"
              class="action-btn"
              @click="showEmailDialog = true"
            />
            <q-btn
              color="secondary"
              icon="comment"
              label="Add Comment"
              class="action-btn"
              @click="showCommentDialog = true"
            />
          </q-btn-group>
        </div>
      </div>
    </div>

    <!-- Communications Timeline -->
    <div class="communications-timeline">
      <q-timeline color="primary" side="right">
        <!-- All Communication Items (sorted by date) -->
        <q-timeline-entry
          v-for="item in allCommunications"
          :key="`${item.type}-${item.id}`"
          :icon="getItemIcon(item)"
          :color="getItemColor(item)"
          :subtitle="formatDate(item.sent_at || item.created_at)"
        >
          <template #title>
            <div class="timeline-title">
              {{ item.subject || (item.type === 'tender_letter' ? 'Tender Letter Sent' : 'Email') }}
              <q-badge
                :color="item.type === 'tender_letter' ? 'primary' : 'accent'"
                :label="item.type === 'tender_letter' ? 'Quotation' : 'Email'"
                class="q-ml-sm"
              />
            </div>
          </template>

          <div class="email-content">
            <div class="email-meta">
              <div class="email-participants">
                <strong>From:</strong> {{ item.from_email }}<br>
                <strong>To:</strong>
                <template v-if="item.contact_name && item.customer_name">
                  {{ item.contact_name }} at {{ item.customer_name }} ({{ item.to_emails?.join(', ') }})
                </template>
                <template v-else-if="item.contact_name">
                  {{ item.contact_name }} ({{ item.to_emails?.join(', ') }})
                </template>
                <template v-else>
                  {{ item.to_emails?.join(', ') || 'No recipients' }}
                </template>
              </div>

              <div class="email-status">
                <q-badge
                  :color="getEmailStatusColor(item.status)"
                  :label="getEmailStatusLabel(item.status)"
                />
              </div>
            </div>

            <div v-if="item.body" class="email-body">
              {{ truncateText(item.body, 200) }}
            </div>

            <div v-if="item.filename" class="email-attachment q-mt-sm">
              <q-icon name="attach_file" size="sm" class="q-mr-xs" />
              <span class="text-caption">{{ item.filename }}</span>
            </div>

            <div class="email-actions">
              <q-btn
                v-if="item.body"
                flat
                size="sm"
                icon="visibility"
                label="View Full"
                @click="viewEmail(item)"
              />
              <q-btn
                v-if="item.type !== 'tender_letter'"
                flat
                size="sm"
                icon="reply"
                label="Reply"
                @click="replyToEmail(item)"
              />
            </div>
          </div>
        </q-timeline-entry>

        <!-- Comment Items -->
        <q-timeline-entry
          v-for="comment in comments"
          :key="`comment-${comment.id}`"
          icon="comment"
          color="secondary"
          :subtitle="formatDate(comment.created_at)"
        >
          <template #title>
            <div class="timeline-title">
              Comment by {{ comment.user_name }}
              <q-badge color="secondary" label="Comment" class="q-ml-sm" />
            </div>
          </template>
          
          <div class="comment-content">
            <div class="comment-text">
              {{ comment.content }}
            </div>
            
            <div class="comment-actions">
              <q-btn
                flat
                size="sm"
                icon="edit"
                label="Edit"
                @click="editComment(comment)"
              />
              <q-btn
                flat
                size="sm"
                icon="delete"
                label="Delete"
                color="negative"
                @click="deleteComment(comment)"
              />
            </div>
          </div>
        </q-timeline-entry>

        <!-- Empty State -->
        <q-timeline-entry
          v-if="allCommunications.length === 0 && comments.length === 0"
          icon="chat_bubble_outline"
          color="grey-5"
        >
          <template #title>
            <div class="timeline-title text-grey-6">
              No communications yet
            </div>
          </template>
          
          <div class="empty-communication">
            <p>Start communicating about this tender by sending an email or adding a comment.</p>
            <q-btn
              color="primary"
              icon="email"
              label="Send First Email"
              size="sm"
              @click="showEmailDialog = true"
            />
          </div>
        </q-timeline-entry>
      </q-timeline>
    </div>

    <!-- Email Dialog -->
    <q-dialog v-model="showEmailDialog" persistent>
      <q-card style="min-width: 600px">
        <q-card-section>
          <div class="text-h6">Send Email</div>
        </q-card-section>
        
        <q-card-section>
          <q-form>
            <div class="q-gutter-md">
              <q-select
                v-model="emailForm.recipients"
                :options="recipientOptions"
                label="Recipients"
                multiple
                use-chips
                outlined
              />
              
              <q-input
                v-model="emailForm.subject"
                label="Subject"
                outlined
              />
              
              <q-input
                v-model="emailForm.body"
                label="Message"
                type="textarea"
                rows="6"
                outlined
              />
              
              <q-file
                v-model="emailForm.attachments"
                label="Attachments"
                multiple
                outlined
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
              />
            </div>
          </q-form>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn 
            label="Send Email" 
            color="primary" 
            :loading="sendingEmail"
            @click="sendEmail"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Comment Dialog -->
    <q-dialog v-model="showCommentDialog" persistent>
      <q-card style="min-width: 500px">
        <q-card-section>
          <div class="text-h6">{{ editingComment ? 'Edit Comment' : 'Add Comment' }}</div>
        </q-card-section>
        
        <q-card-section>
          <q-input
            v-model="commentForm.content"
            label="Comment"
            type="textarea"
            rows="4"
            outlined
            autofocus
          />
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="cancelComment" />
          <q-btn 
            :label="editingComment ? 'Update Comment' : 'Add Comment'" 
            color="secondary" 
            :loading="savingComment"
            @click="saveComment"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Email View Dialog -->
    <q-dialog v-model="showEmailViewDialog">
      <q-card style="min-width: 700px; max-width: 90vw">
        <q-card-section v-if="viewingEmail">
          <div class="text-h6">{{ viewingEmail.subject }}</div>
          <div class="email-meta q-mt-sm">
            <div><strong>From:</strong> {{ viewingEmail.from_email }}</div>
            <div><strong>To:</strong> {{ viewingEmail.to_emails?.join(', ') }}</div>
            <div><strong>Sent:</strong> {{ formatDate(viewingEmail.sent_at) }}</div>
          </div>
        </q-card-section>
        
        <q-card-section v-if="viewingEmail">
          <div class="email-body-full">{{ viewingEmail.body }}</div>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Close" />
          <q-btn 
            label="Reply" 
            color="primary" 
            @click="replyToEmail(viewingEmail)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    tender: {
      type: Object,
      required: true,
    },
  });

  defineEmits(['send-email', 'add-comment']);

  const $q = useQuasar();

  // State
  const showEmailDialog = ref(false);
  const showCommentDialog = ref(false);
  const showEmailViewDialog = ref(false);
  const sendingEmail = ref(false);
  const savingComment = ref(false);
  const editingComment = ref(null);
  const viewingEmail = ref(null);
  const loadingEmails = ref(false);

  // Real data from API
  const emails = ref([]);
  const tenderLetterEmails = ref([]);
  const comments = ref([]);

  // Computed - merge all communications and sort by date
  const allCommunications = computed(() => {
    const all = [
      ...emails.value.map((e) => ({ ...e, type: 'email' })),
      ...tenderLetterEmails.value,
    ];
    return all.sort((a, b) => new Date(b.sent_at || b.created_at) - new Date(a.sent_at || a.created_at));
  });

  // Form data
  const emailForm = ref({
    recipients: [],
    subject: '',
    body: '',
    attachments: null,
  });

  const commentForm = ref({
    content: '',
  });

  // Computed properties - Build recipient options from contractors, consultant, and builder
  const recipientOptions = computed(() => {
    const options = [];

    // Add contractors and their contacts
    const contractors = props.tender.contractors || [];
    for (const contractor of contractors) {
      const companyName = contractor.effective_company_name || contractor.company_name || contractor.business_name || contractor.name;
      // Get contacts from the contractor (organization contacts or legacy contacts)
      const contacts = contractor.contacts || [];
      for (const contact of contacts) {
        if (contact.email) {
          options.push({
            label: `${companyName} - ${contact.name} (${contact.email})`,
            value: contact.email,
            contactName: contact.name,
            customerName: companyName,
            type: 'contractor',
          });
        }
      }
      // If contractor has direct email but no contacts
      if (contacts.length === 0 && (contractor.effective_email || contractor.email)) {
        const email = contractor.effective_email || contractor.email;
        options.push({
          label: `${companyName} (${email})`,
          value: email,
          contactName: contractor.effective_contact_name || contractor.contact_name || 'Primary',
          customerName: companyName,
          type: 'contractor',
        });
      }
    }

    // Add consultant contacts
    const consultant = props.tender.consultant;
    if (consultant) {
      const consultantContacts = consultant.contacts || [];
      for (const contact of consultantContacts) {
        if (contact.email) {
          options.push({
            label: `[Consultant] ${consultant.name} - ${contact.name} (${contact.email})`,
            value: contact.email,
            contactName: contact.name,
            customerName: consultant.name,
            type: 'consultant',
          });
        }
      }
      // If consultant has primary contact but no contacts array
      if (consultantContacts.length === 0 && consultant.primary_contact?.email) {
        options.push({
          label: `[Consultant] ${consultant.name} - ${consultant.primary_contact.name} (${consultant.primary_contact.email})`,
          value: consultant.primary_contact.email,
          contactName: consultant.primary_contact.name,
          customerName: consultant.name,
          type: 'consultant',
        });
      }
    }

    // Add builder contacts
    const builder = props.tender.builder;
    if (builder) {
      const builderContacts = builder.contacts || [];
      for (const contact of builderContacts) {
        if (contact.email) {
          options.push({
            label: `[Builder] ${builder.name} - ${contact.name} (${contact.email})`,
            value: contact.email,
            contactName: contact.name,
            customerName: builder.name,
            type: 'builder',
          });
        }
      }
      // If builder has primary contact but no contacts array
      if (builderContacts.length === 0 && builder.primary_contact?.email) {
        options.push({
          label: `[Builder] ${builder.name} - ${builder.primary_contact.name} (${builder.primary_contact.email})`,
          value: builder.primary_contact.email,
          contactName: builder.primary_contact.name,
          customerName: builder.name,
          type: 'builder',
        });
      }
    }

    return options;
  });

  // Methods
  function getItemIcon(item) {
    if (item.type === 'tender_letter') {return 'description';}
    return item.type === 'inbound' ? 'inbox' : 'send';
  }

  function getItemColor(item) {
    if (item.type === 'tender_letter') {return 'primary';}
    return item.type === 'inbound' ? 'positive' : 'accent';
  }

  function getEmailIcon(email) {
    return email.type === 'inbound' ? 'inbox' : 'send';
  }

  function getEmailColor(email) {
    return email.type === 'inbound' ? 'positive' : 'accent';
  }

  function getEmailStatusColor(status) {
    const colors = {
      sent: 'positive',
      delivered: 'blue',
      opened: 'orange',
      failed: 'negative',
    };
    return colors[status] || 'grey';
  }

  function getEmailStatusLabel(status) {
    const labels = {
      sent: 'Sent',
      delivered: 'Delivered',
      opened: 'Opened',
      failed: 'Failed',
    };
    return labels[status] || status;
  }

  function formatDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function truncateText(text, length) {
    if (!text) {return '';}
    return text.length > length ? text.substring(0, length) + '...' : text;
  }

  function viewEmail(email) {
    viewingEmail.value = email;
    showEmailViewDialog.value = true;
  }

  function replyToEmail(email) {
    emailForm.value = {
      recipients: [email.from_email],
      subject: `Re: ${email.subject}`,
      body: '',
      attachments: null,
    };
    showEmailViewDialog.value = false;
    showEmailDialog.value = true;
  }

  async function sendEmail() {
    if (!emailForm.value.recipients.length) {
      $q.notify({
        color: 'warning',
        message: 'Please select at least one recipient',
        icon: 'warning',
      });
      return;
    }

    sendingEmail.value = true;
    try {
      // Get recipient emails (could be strings or objects with value property)
      const recipients = emailForm.value.recipients.map((r) => (typeof r === 'string' ? r : r.value || r));

      // Get contact name from first selected recipient for personalization
      const firstRecipient = emailForm.value.recipients[0];
      const contactName = typeof firstRecipient === 'object' ? firstRecipient.contactName : 'Valued Customer';

      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/send-email`, {
        method: 'POST',
        body: JSON.stringify({
          subject: emailForm.value.subject,
          recipients: recipients,
          body: emailForm.value.body,
          contact_name: contactName,
          use_template: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        $q.notify({
          color: 'positive',
          message: 'Email sent successfully',
          icon: 'email',
        });

        // Reload email history
        await loadEmailDrafts();

        // Reset form
        emailForm.value = {
          recipients: [],
          subject: '',
          body: '',
          attachments: null,
        };

        showEmailDialog.value = false;
      } else {
        throw new Error(data.message || data.error || 'Failed to send email');
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          color: 'negative',
          message: 'Failed to send email: ' + error.message,
          icon: 'error',
        });
      }
    } finally {
      sendingEmail.value = false;
    }
  }

  // Load email history from API
  async function loadEmailDrafts() {
    loadingEmails.value = true;
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/email-drafts`);
      const data = await response.json();
      // Transform the data to match our display format
      const drafts = data.data || data || [];
      emails.value = drafts.map((draft) => ({
        id: draft.id,
        subject: draft.subject,
        from_email: 'sales@bar-tech.com.au',
        to_emails: draft.recipients || [],
        body: draft.body || '',
        status: draft.status,
        sent_at: draft.sent_at || draft.created_at,
        type: 'outbound',
      }));
    } catch (error) {
      console.error('Failed to load email history:', error);
      emails.value = [];
    } finally {
      loadingEmails.value = false;
    }
  }

  // Load tender letter email logs from API
  async function loadTenderLetterEmails() {
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/letter-emails`);
      const data = await response.json();
      tenderLetterEmails.value = data.data || [];
    } catch (error) {
      console.error('Failed to load tender letter emails:', error);
      tenderLetterEmails.value = [];
    }
  }

  // Load comments from API
  async function loadComments() {
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}?include=comments`);
      const data = await response.json();
      const tenderComments = data.data?.comments || [];
      comments.value = tenderComments.map((c) => ({
        id: c.id,
        content: c.content,
        user_name: c.user?.name || 'Unknown',
        created_at: c.created_at,
      }));
    } catch (error) {
      console.error('Failed to load comments:', error);
      comments.value = [];
    }
  }

  function editComment(comment) {
    editingComment.value = comment;
    commentForm.value.content = comment.content;
    showCommentDialog.value = true;
  }

  async function saveComment() {
    if (!commentForm.value.content.trim()) {
      $q.notify({
        color: 'warning',
        message: 'Please enter a comment',
        icon: 'warning',
      });
      return;
    }

    savingComment.value = true;
    try {
      if (editingComment.value) {
        // Update existing comment
        const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/comments/${editingComment.value.id}`, {
          method: 'PUT',
          body: JSON.stringify({ content: commentForm.value.content }),
        });
        const data = await response.json();

        if (data.success) {
          $q.notify({
            color: 'positive',
            message: 'Comment updated successfully',
            icon: 'comment',
          });
          await loadComments();
        } else {
          throw new Error(data.message || 'Failed to update comment');
        }
      } else {
        // Add new comment
        const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/comments`, {
          method: 'POST',
          body: JSON.stringify({ content: commentForm.value.content }),
        });
        const data = await response.json();

        if (data.success) {
          $q.notify({
            color: 'positive',
            message: 'Comment added successfully',
            icon: 'comment',
          });
          await loadComments();
        } else {
          throw new Error(data.message || 'Failed to add comment');
        }
      }

      cancelComment();
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          color: 'negative',
          message: 'Failed to save comment: ' + error.message,
          icon: 'error',
        });
      }
    } finally {
      savingComment.value = false;
    }
  }

  function cancelComment() {
    commentForm.value.content = '';
    editingComment.value = null;
    showCommentDialog.value = false;
  }

  function deleteComment(comment) {
    $q.dialog({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/comments/${comment.id}`, {
          method: 'DELETE',
        });
        const data = await response.json();

        if (data.success) {
          $q.notify({
            color: 'positive',
            message: 'Comment deleted',
            icon: 'delete',
          });
          await loadComments();
        } else {
          throw new Error(data.message || 'Failed to delete comment');
        }
      } catch (error) {
        if (!error.isAuthError) {
          $q.notify({
            color: 'negative',
            message: 'Failed to delete comment: ' + error.message,
            icon: 'error',
          });
        }
      }
    });
  }

  onMounted(async () => {
    // Load communications data from API
    await Promise.all([
      loadEmailDrafts(),
      loadTenderLetterEmails(),
      loadComments(),
    ]);
  });

  // Watch for tender changes to reload data
  watch(() => props.tenderId, async () => {
    await Promise.all([
      loadEmailDrafts(),
      loadTenderLetterEmails(),
      loadComments(),
    ]);
  });
</script>

<style lang="scss" scoped>
.tender-communications-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
    
    .action-btn {
      font-weight: 600;
      border-radius: 8px;
    }
  }
  
  .communications-timeline {
    .timeline-title {
      font-weight: 600;
      color: #333;
      display: flex;
      align-items: center;
    }
    
    .email-content,
    .comment-content {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      margin-top: 8px;
      
      .email-meta {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
        font-size: 0.9rem;
        
        .email-participants {
          color: #666;
          line-height: 1.4;
        }
      }
      
      .email-body,
      .comment-text {
        color: #333;
        line-height: 1.5;
        margin-bottom: 12px;
      }
      
      .email-actions,
      .comment-actions {
        display: flex;
        gap: 8px;
      }
    }
    
    .empty-communication {
      color: #666;
      font-style: italic;
      text-align: center;
      padding: 16px;
    }
  }
  
  .email-body-full {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    line-height: 1.5;
    white-space: pre-wrap;
  }
}
</style>